import { useState, useEffect } from 'react';
import { Mail, Loader2, AlertCircle, CheckCircle2, XCircle, Building2, Plus, Trash2 } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { notifyApprovalsChanged } from '@/lib/usePendingApprovalCounts';

interface Pending {
  id: string;
  domain: string;
  proposed_at: string;
  notes: string | null;
  member_id: string;
  organization_name: string;
  study_association_name: string;
  proposed_by_email: string | null;
  proposed_by_name: string | null;
}

interface Member {
  id: string;
  organization_name: string;
}

interface DomainRow {
  id: string;
  domain: string;
  status: 'pending' | 'approved' | 'rejected';
  proposed_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
  proposed_by_name: string | null;
  reviewed_by_name: string | null;
}

export default function PendingDomainsAdmin() {
  const [pending, setPending] = useState<Pending[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Add-domain state
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newDomainNotes, setNewDomainNotes] = useState('');
  const [adding, setAdding] = useState(false);

  // Per-member domain list (loaded on member-select)
  const [memberDomains, setMemberDomains] = useState<DomainRow[]>([]);
  const [loadingMemberDomains, setLoadingMemberDomains] = useState(false);

  useEffect(() => {
    load();
    loadMembers();
  }, []);

  useEffect(() => {
    if (selectedMemberId) loadMemberDomains(selectedMemberId);
    else setMemberDomains([]);
  }, [selectedMemberId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch('/api/admin/email-domains/pending');
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke laste');
      setPending(d.pending || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMembers() {
    try {
      const r = await authenticatedFetch('/api/admin/members');
      const d = await r.json();
      if (!r.ok) return;
      setMembers((d.members || []).map((m: any) => ({ id: m.id, organization_name: m.organization_name })));
    } catch {
      // soft fail — felt skjules
    }
  }

  async function loadMemberDomains(memberId: string) {
    setLoadingMemberDomains(true);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/email-domains`);
      const d = await r.json();
      if (r.ok) setMemberDomains(d.domains || []);
    } catch {
      // soft fail
    } finally {
      setLoadingMemberDomains(false);
    }
  }

  async function addDomain() {
    setError(null);
    setSuccess(null);
    if (!selectedMemberId) { setError('Velg en medlemsorganisasjon'); return; }
    if (!newDomain.trim()) { setError('Skriv inn et domene (eksempel: byggmesterforbundet.no)'); return; }
    setAdding(true);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${selectedMemberId}/email-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim().replace(/^@/, ''), notes: newDomainNotes.trim() || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke legge til domene');
      setSuccess(`Domene "${newDomain.trim()}" er godkjent. Du kan nå invitere brukere med denne e-postendelsen.`);
      setNewDomain('');
      setNewDomainNotes('');
      await loadMemberDomains(selectedMemberId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  }

  async function deleteDomain(id: string) {
    if (!confirm('Slett dette domenet? Brukere kan ikke lenger inviteres med denne e-postendelsen.')) return;
    setActingId(id);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/email-domains/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess('Domene slettet.');
      if (selectedMemberId) await loadMemberDomains(selectedMemberId);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActingId(null);
    }
  }

  async function approve(id: string) {
    setActingId(id);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/email-domains/${id}/approve`, { method: 'PATCH' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess('Domene godkjent. Foreslår er varslet.');
      notifyApprovalsChanged();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActingId(null);
    }
  }

  async function reject(id: string) {
    setActingId(id);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/email-domains/${id}/reject`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess('Domene avvist. Foreslår er varslet.');
      setRejectingId(null);
      setRejectReason('');
      notifyApprovalsChanged();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Legg til godkjent domene */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Plus size={22} />
            Legg til godkjent e-postdomene
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Som studieforbund- eller systemadministrator kan du legge til e-postdomener direkte for en medlemsorganisasjon.
            Domenet brukes til å validere e-postadresser ved invitasjon av brukere — for eksempel <code className="bg-gray-100 px-1 rounded">byggmesterforbundet.no</code> hvis dere skal invitere brukere med <em>navn@byggmesterforbundet.no</em>.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 m-0 whitespace-pre-line">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800 m-0">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medlemsorganisasjon</label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">— velg organisasjon —</option>
              {members.sort((a, b) => a.organization_name.localeCompare(b.organization_name)).map(m => (
                <option key={m.id} value={m.id}>{m.organization_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-postdomene</label>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="byggmesterforbundet.no"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notat (valgfritt)</label>
            <input
              type="text"
              value={newDomainNotes}
              onChange={(e) => setNewDomainNotes(e.target.value)}
              placeholder="Eks. 'Hoveddomene'"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={addDomain}
            disabled={adding || !selectedMemberId || !newDomain.trim()}
            className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-primary/90 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
          >
            {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Legg til (godkjent)
          </button>
        </div>

        {/* Liste over allerede registrerte domener for valgt org */}
        {selectedMemberId && (
          <div className="mt-5 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">Registrerte domener for valgt organisasjon</div>
            {loadingMemberDomains ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            ) : memberDomains.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Ingen domener registrert ennå.</p>
            ) : (
              <ul className="space-y-1.5">
                {memberDomains.map(d => (
                  <li key={d.id} className="flex items-center justify-between gap-3 text-sm border border-gray-200 rounded px-3 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono">@{d.domain}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        d.status === 'approved' ? 'bg-green-100 text-green-800' :
                        d.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {d.status === 'approved' ? 'Godkjent' : d.status === 'pending' ? 'Venter' : 'Avvist'}
                      </span>
                      {d.notes && <span className="text-xs text-gray-500 truncate italic">{d.notes}</span>}
                    </div>
                    <button
                      onClick={() => deleteDomain(d.id)}
                      disabled={actingId === d.id}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                      title="Slett domene"
                    >
                      {actingId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Ventende forslag fra medlem-admin */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Mail size={22} />
            Ventende domeneforslag fra medlem-administratorer
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Medlemsorganisasjoner kan foreslå e-postdomener som skal være tillatt for invitasjon av brukere.
            Godkjenn eller avvis forslag her.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : pending.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-300" />
            <p>Ingen ventende forslag.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((p) => (
              <div key={p.id} className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <strong className="text-lg font-mono">@{p.domain}</strong>
                      <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-900 rounded-full">Venter</span>
                    </div>
                    <div className="text-sm text-gray-700 flex items-center gap-2 mb-1">
                      <Building2 size={14} />
                      <span>{p.organization_name}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{p.study_association_name}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Foreslått {new Date(p.proposed_at).toLocaleDateString('nb-NO')}
                      {p.proposed_by_name ? ` av ${p.proposed_by_name}` : ''}
                      {p.proposed_by_email ? ` (${p.proposed_by_email})` : ''}
                    </div>
                    {p.notes && (
                      <p className="text-sm text-gray-700 mt-2 italic bg-white/60 rounded p-2">{p.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => approve(p.id)}
                      disabled={actingId === p.id}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                    >
                      {actingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Godkjenn
                    </button>
                    <button
                      onClick={() => { setRejectingId(p.id); setRejectReason(''); }}
                      disabled={actingId === p.id}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                    >
                      <XCircle size={14} />
                      Avvis
                    </button>
                  </div>
                </div>

                {rejectingId === p.id && (
                  <div className="mt-3 pt-3 border-t border-yellow-300">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Begrunnelse (sendes til foreslår)
                    </label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      placeholder="F.eks. 'Domenet tilhører ikke organisasjonen'"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => { setRejectingId(null); setRejectReason(''); }}
                        className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded"
                      >
                        Avbryt
                      </button>
                      <button
                        onClick={() => reject(p.id)}
                        disabled={actingId === p.id}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded disabled:opacity-50"
                      >
                        Bekreft avvisning
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
